import { useDateRange } from '../context/DateRangeContext';
import { AnalyticsChartsSection } from './components/ChartsSection';
import { KpiSection } from './components/KpiSection';
import { SourceUrlsSection } from './components/SourceUrlsSection';
import { UserIntentChart } from './components/UserIntentChart';
import { useAnalyticsInsights } from './hooks/useAnalyticsInsights.hook';
import { useUserIntentDistribution } from './hooks/useUserIntentDistribution.hook';
import { DateRangeControls } from '@/Domain/components/Shared/DateRangeControls/DateRangeControls';
import { useConversationAnalytics } from '@/Domain/hooks/Analytics/useConversationAnalytics.hook';
import { useSourceUrls } from '@/Domain/hooks/Analytics/useSourceUrls.hook';

export const AnalyticsInsightsPage = () => {
  const { range, handleRangeChange } = useDateRange();
  const {
    outcomeLabels,
    outcomeTotals,
    isLoading: isConversationAnalyticsLoading,
  } = useConversationAnalytics({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const { userIntentCounts, isLoading: isUserIntentLoading } = useUserIntentDistribution({
    startDate: range.startDate,
    endDate: range.endDate,
  });
  const {
    data: sourceUrlsData,
    isLoading: isSourceUrlsLoading,
    isError: isSourceUrlsError,
    pagination: sourceUrlsPagination,
    setPagination: setSourceUrlsPagination,
  } = useSourceUrls(range);
  const {
    isLoading,
    conversations,
    conversationsPercentChange,
    conversationsByFormat,
    totalMessages,
    avgMessagesPerConversation,
    messages,
    messagesPercentChange,
    handoffNow,
    handoffPercentChange,
    handoffRatePercentChange,
    handoffConversationsByFormat,
    conversationsByDay,
    messagesByDay,
    conversationsByWeekday,
    conversationsByHour,
    conversationsBySource,
    conversationsByLanguage,
    conversationsByMessages,
    durationBuckets,
    durationCutoff,
    durationOutliers,
  } = useAnalyticsInsights(range);

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-6">
      {/* Date Range Controls */}
      <DateRangeControls range={range} onRangeChange={handleRangeChange} />

      {/* KPI Section */}
      <KpiSection
        isLoading={isLoading}
        conversations={conversations}
        conversationsPercentChange={conversationsPercentChange}
        conversationsByFormat={conversationsByFormat}
        totalMessages={totalMessages}
        avgMessagesPerConversation={avgMessagesPerConversation}
        messages={messages}
        messagesPercentChange={messagesPercentChange}
        handoffNow={handoffNow}
        handoffPercentChange={handoffPercentChange}
        handoffRatePercentChange={handoffRatePercentChange}
        handoffConversationsByFormat={handoffConversationsByFormat}
      />

      {/* Charts Section */}
      <AnalyticsChartsSection
        isLoading={isLoading}
        outcomeTotals={outcomeTotals}
        outcomeLabels={outcomeLabels}
        isOutcomeLoading={isConversationAnalyticsLoading}
        conversationsByDay={conversationsByDay}
        messagesByDay={messagesByDay}
        conversationsByWeekday={conversationsByWeekday}
        conversationsByHour={conversationsByHour}
        conversationsBySource={conversationsBySource}
        conversationsByLanguage={conversationsByLanguage}
        conversationsByMessages={conversationsByMessages}
        durationBuckets={durationBuckets}
        durationCutoff={durationCutoff}
        durationOutliers={durationOutliers}
      />

      {/* User Intent Pie Chart */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <UserIntentChart userIntentCounts={userIntentCounts} isLoading={isUserIntentLoading} />
      </div>

      {/* Source URLs Table */}
      <SourceUrlsSection
        isError={isSourceUrlsError}
        isLoading={isSourceUrlsLoading}
        data={sourceUrlsData}
        pagination={sourceUrlsPagination}
        setPagination={setSourceUrlsPagination}
      />
    </div>
  );
};
